/**
 * `/login` — the single-credential sign-in form used in "simple" auth mode.
 * Posts to the API, stores the returned session token, and returns the user to
 * wherever they were headed. In skyagent mode, short-circuits to the external
 * handoff instead of rendering the form.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAuthMode, loginWithPassword, isValidRedirectUrl, redirectToLogin } from "@/lib/auth";
import { useAuth } from "@/lib/auth/useAuth";
import { panelErrorTitle } from "@/lib/api-client";

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, refresh } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnToRaw = params.get("returnTo");
  const returnTo = isValidRedirectUrl(returnToRaw) ? returnToRaw : "/home";
  const mode = getAuthMode();

  // In skyagent mode, redirect to the handoff — never render the simple form.
  useEffect(() => {
    if (mode === "skyagent") {
      redirectToLogin(returnTo);
    }
  }, [mode, returnTo]);

  // Already signed in (valid cookie, or disabled mode) → leave the form. Done as
  // an effect so it fires once, not on every keystroke/re-render.
  useEffect(() => {
    if (isAuthenticated) navigate(returnTo, { replace: true });
  }, [isAuthenticated, navigate, returnTo]);

  // Don't render the form for an already-authenticated user (avoids a flash),
  // or when in skyagent mode (redirect is in flight).
  if (isAuthenticated || mode === "skyagent") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-fg-faint">
        Redirecting…
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await loginWithPassword(username.trim(), password);
      refresh();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(panelErrorTitle(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-xl border border-border-subtle bg-surface-card p-6 shadow-sm"
      >
        <h1 className="text-base font-semibold tracking-tight text-fg-primary">
          Operations <span className="text-info-text">Dashboard</span>
        </h1>
        <p className="mt-1 text-xs text-fg-faint">Sign in to continue.</p>

        <label className="mt-5 block text-xs font-medium text-fg-muted" htmlFor="username">
          Username
        </label>
        <input
          id="username"
          name="username"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoFocus
          className="mt-1 w-full rounded-md border border-border-subtle bg-surface-inset px-3 py-2 text-sm text-fg-primary outline-none focus:border-info focus:ring-1 focus:ring-info"
        />

        <label className="mt-4 block text-xs font-medium text-fg-muted" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="mt-1 w-full rounded-md border border-border-subtle bg-surface-inset px-3 py-2 text-sm text-fg-primary outline-none focus:border-info focus:ring-1 focus:ring-info"
        />

        {error && (
          <p className="mt-3 rounded-md border border-danger bg-danger/10 px-3 py-2 text-xs text-danger-text">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !username || !password}
          className="mt-5 w-full rounded-md bg-info px-3 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
